import { Fragment, useCallback, useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { useFleetApi, type KillSwitch } from "./api";

// Action endpoints defined under fleet/actions/. Shown as "not registered yet"
// when no kill_switch row exists; flipping creates the row.
const KNOWN_ENDPOINTS = [
  "gmail_send_reply",
  "gmail_label",
  "stripe_refund",
  "shopify_refund",
  "wholesale_reship",
  "wholesale_update_address",
  "wholesale_quote_send",
  "ads_add_credit",
];

function fmtTs(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

interface Row {
  endpoint: string;
  enabled: boolean;
  reason: string | null;
  updated_at: string | null;
  updated_by: string | null;
  registered: boolean;
}

export default function Settings() {
  const { token, logout, user } = useAuth();
  const { fetchKillSwitches, flipKillSwitch } = useFleetApi(token, logout);
  const [rows, setRows] = useState<KillSwitch[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busyEp, setBusyEp] = useState<string | null>(null);

  const actor = user?.username ?? "arthur";

  // `fetchKillSwitches` is rebuilt when the session token changes, so depending
  // on it keeps the reload-on-token behaviour without an explicit token dep.
  const load = useCallback(() => {
    setErr(null);
    fetchKillSwitches()
      .then(r => setRows(r))
      .catch(e => setErr(String(e)));
  }, [fetchKillSwitches]);

  useEffect(() => { load(); }, [load]);

  const handleFlip = async (endpoint: string, nextEnabled: boolean) => {
    setBusyEp(endpoint);
    try {
      await flipKillSwitch({
        endpoint,
        enabled: nextEnabled,
        reason: nextEnabled ? null : "disabled via Settings UI",
        actor,
      });
      load();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusyEp(null);
    }
  };

  const byEndpoint: Map<string, KillSwitch> = new Map((rows ?? []).map(r => [r.endpoint, r]));
  const seen = new Set(byEndpoint.keys());
  const display: Row[] = [
    ...(rows ?? []).map(r => ({ ...r, registered: true })),
    ...KNOWN_ENDPOINTS
      .filter(ep => !seen.has(ep))
      .map(ep => ({
        endpoint: ep,
        enabled: true,
        reason: null,
        updated_at: null,
        updated_by: null,
        registered: false,
      })),
  ];

  return (
    <section>
      <span className="meta">
        {rows == null ? "loading…" : `${rows.length} switch${rows.length === 1 ? "" : "es"} configured`}
      </span>
      <p className="page-lede">
        Kill switches let you disable individual <code>/fleet/actions/*</code> endpoints.
        A disabled endpoint returns 503; the runtime treats that as a forced escalation.
      </p>

      {err && <p style={{ color: "var(--crit)", fontSize: "12.5px" }}>{err}</p>}

      <div
        className="card"
        style={{
          display: "grid",
          gridTemplateColumns: "1.4fr auto 1fr auto",
          rowGap: "10px",
          columnGap: "16px",
          alignItems: "center",
        }}
      >
        <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-3)" }}>Endpoint</div>
        <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-3)" }}>State</div>
        <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-3)" }}>Updated</div>
        <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-3)" }}>Action</div>

        {display.map(row => (
          <Fragment key={row.endpoint}>
            <div><code>{row.endpoint}</code></div>
            <div>
              {row.registered ? (
                <span className={`pill ${row.enabled ? "ok" : "crit"}`}>
                  {row.enabled ? "enabled" : "disabled"}
                </span>
              ) : (
                <span className="pill muted">not yet flipped</span>
              )}
            </div>
            <div style={{ fontSize: "12.5px", color: "var(--text-2)" }}>
              {row.registered ? (
                <>
                  {fmtTs(row.updated_at)}
                  {row.updated_by && <span style={{ marginLeft: "6px", color: "var(--text-3)" }}>by {row.updated_by}</span>}
                  {row.reason && (
                    <div style={{ fontSize: "11.5px", color: "var(--text-3)", marginTop: "2px" }}>{row.reason}</div>
                  )}
                </>
              ) : "—"}
            </div>
            <div>
              {row.enabled ? (
                <button
                  className="opt crit"
                  disabled={busyEp === row.endpoint}
                  onClick={() => handleFlip(row.endpoint, false)}
                >
                  Disable
                </button>
              ) : (
                <button
                  className="opt"
                  disabled={busyEp === row.endpoint}
                  onClick={() => handleFlip(row.endpoint, true)}
                >
                  Re-enable
                </button>
              )}
            </div>
          </Fragment>
        ))}
      </div>
    </section>
  );
}
